import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { get, post } from '../api';
import { getLang, LangSwitch, msg, tr } from '../i18n';
import { useSession } from '../session';
import { BrandMark, Button, Field, friendlyError, ThemeSwitch } from '../ui';

function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-art">
        <div className="row" style={{ gap: 10 }}>
          <BrandMark size={34} />
          <span className="brand-name">ZeroGap</span>
        </div>
        <div>
          <h1>{tr('Every file, accounted for.')}</h1>
          <p>{tr('Simple, safe backups for the computers in your business.')}</p>
        </div>
        <div className="ledger-lines">
          <div>✓ {tr('Backs up your computers automatically, several times a day')}</div>
          <div>✓ {tr('Get any file back as it was, even if the computer is lost')}</div>
          <div>✓ {tr('Files are encrypted before they leave the computer')}</div>
        </div>
      </div>
      <div className="auth-form">
        <div className="lang-switch">
          <ThemeSwitch className="btn sm ghost" />
          <LangSwitch className="btn sm ghost" />
        </div>
        {children}
        <a className="auth-legal" href="/accessibility">{tr('Accessibility statement')}</a>
      </div>
    </div>
  );
}

const GOOGLE_ERRORS: Record<string, string> = {
  google_expired: msg('Signing in with Google took too long. Please try again.'),
  google_cancelled: msg('Signing in with Google was canceled.'),
  google_failed: msg('We could not sign you in with Google. Please try again.'),
  google_unverified: msg('Google has not confirmed this email address, so it cannot be used to sign in.'),
  google_disabled: msg('This account is turned off. Ask your organization’s admin for help.'),
  google_other_account: msg('This email address is already connected to a different Google account. Sign in with your password instead.'),
};
// Server messages from the Google routes, translated where they are shown (friendlyError).
msg('This sign-up has expired. Continue with Google again.');
msg('Confirm it is you by signing in with Google again');

/** The official four-color Google "G". */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** "Continue with Google", shown only when the server has Google sign-in configured. */
function GoogleButton({ next, invite }: { next?: string | null; invite?: string }) {
  const providers = useQuery({ queryKey: ['auth-providers'], queryFn: () => get<{ google: boolean }>('/api/auth/providers'), staleTime: Infinity, retry: false });
  if (!providers.data?.google) return null;
  const q = new URLSearchParams({ client: 'web' });
  if (next) q.set('next', next);
  if (invite) q.set('invite', invite);
  return (
    <>
      <a className="btn google" href={`/api/auth/google/start?${q}`}>
        <GoogleMark />
        {tr('Continue with Google')}
      </a>
      <div className="auth-or"><span>{tr('or')}</span></div>
    </>
  );
}

function useSubmit<T>(fn: () => Promise<T>) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      return await fn();
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, submit };
}

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { refresh } = useSession();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const s = useSubmit(async () => {
    const r = await post<{ mfaRequired: boolean }>('/api/auth/login', { email, password });
    await refresh();
    nav(r.mfaRequired ? `/mfa?next=${encodeURIComponent(sp.get('next') ?? '/')}` : sp.get('next') ?? '/');
  });
  const googleErr = GOOGLE_ERRORS[sp.get('error') ?? ''];
  return (
    <AuthLayout>
      <form onSubmit={s.submit}>
        <h2>{tr('Sign in')}</h2>
        <p className="muted" style={{ marginTop: 0 }}>{tr('Welcome back.')}</p>
        {googleErr && <div className="notice bad" style={{ marginBottom: 12 }}>{tr(googleErr)}</div>}
        <GoogleButton next={sp.get('next')} />
        <Field label={tr('Email')}><input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
        <Field label={tr('Password')}><input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {s.err && <div className="notice bad" style={{ marginBottom: 12 }}>{s.err}</div>}
        <Button variant="primary" busy={s.busy} type="submit" style={{ width: '100%' }}>{tr('Sign in')}</Button>
        <div className="row between small" style={{ marginTop: 14 }}>
          <Link to="/forgot-password">{tr('Forgot password?')}</Link>
          <Link to="/signup">{tr('Create an account')}</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function MfaVerify() {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const { refresh } = useSession();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const s = useSubmit(async () => {
    await post('/api/auth/mfa/verify', useRecovery ? { recoveryCode: code } : { code });
    await refresh();
    nav(sp.get('next') ?? '/');
  });
  return (
    <AuthLayout>
      <form onSubmit={s.submit}>
        <h2>{tr('Two-factor verification')}</h2>
        <p className="muted" style={{ marginTop: 0 }}>{useRecovery ? tr('Enter one of your recovery codes.') : tr('Enter the 6-digit code from your authenticator app.')}</p>
        <Field label={useRecovery ? tr('Recovery code') : tr('Code')}>
          <input type="text" inputMode={useRecovery ? 'text' : 'numeric'} autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value.trim())} autoFocus />
        </Field>
        {s.err && <div className="notice bad" style={{ marginBottom: 12 }}>{s.err}</div>}
        <Button variant="primary" busy={s.busy} type="submit" style={{ width: '100%' }}>{tr('Verify')}</Button>
        <p className="small" style={{ marginTop: 14 }}>
          <a href="#" onClick={(e) => { e.preventDefault(); setUseRecovery(!useRecovery); setCode(''); }}>{useRecovery ? tr('Use an authenticator code') : tr('Use a recovery code instead')}</a>
        </p>
      </form>
    </AuthLayout>
  );
}

export function Signup() {
  const [f, setF] = useState({ name: '', email: '', password: '', organizationName: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { refresh } = useSession();
  const nav = useNavigate();
  const s = useSubmit(async () => {
    const r = await post<{ tenantId: string }>('/api/auth/signup', { ...f, acceptTerms, language: getLang() });
    await refresh();
    nav(`/t/${r.tenantId}`);
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <AuthLayout>
      <form onSubmit={s.submit}>
        <h2>{tr('Start protecting your business')}</h2>
        <p className="muted" style={{ marginTop: 0 }}>{tr('14-day trial. No card required.')}</p>
        <GoogleButton />
        <Field label={tr('Your name')}><input type="text" required autoComplete="name" value={f.name} onChange={set('name')} /></Field>
        <Field label={tr('Organization')}><input type="text" required value={f.organizationName} onChange={set('organizationName')} /></Field>
        <Field label={tr('Work email')}><input type="email" required autoComplete="email" value={f.email} onChange={set('email')} /></Field>
        <Field label={tr('Password')} hint={tr('At least 12 characters. A passphrase works well.')}><input type="password" required minLength={12} autoComplete="new-password" value={f.password} onChange={set('password')} /></Field>
        <label className="check">
          <input type="checkbox" required checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
          <span>
            {tr('I have read and agree, on behalf of the organization, to the')}{' '}
            <a href="/terms" target="_blank" rel="noopener">{tr('Terms of Service and Privacy Policy')}</a>
          </span>
        </label>
        {s.err && <div className="notice bad" style={{ marginBottom: 12 }}>{s.err}</div>}
        <Button variant="primary" busy={s.busy} type="submit" style={{ width: '100%' }}>{tr('Create account')}</Button>
        <p className="small" style={{ marginTop: 14 }}>{tr('Already have an account?')} <Link to="/login">{tr('Sign in')}</Link></p>
      </form>
    </AuthLayout>
  );
}

/** After "Continue with Google" for someone with no account yet: name the organization and accept the terms. */
export function GoogleSignup() {
  const [sp] = useSearchParams();
  const ticket = sp.get('ticket') ?? '';
  const [info, setInfo] = useState<{ email: string; name: string } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { refresh } = useSession();
  const nav = useNavigate();
  useEffect(() => {
    get<{ email: string; name: string }>(`/api/auth/google/signup/${encodeURIComponent(ticket)}`).then(setInfo).catch((e) => setLoadErr(friendlyError(e)));
  }, [ticket]);
  const s = useSubmit(async () => {
    const r = await post<{ tenantId: string }>('/api/auth/google/signup', { ticket, organizationName, acceptTerms, language: getLang() });
    await refresh();
    nav(`/t/${r.tenantId}`);
  });
  return (
    <AuthLayout>
      <form onSubmit={s.submit}>
        <h2>{tr('One last step')}</h2>
        {loadErr ? (
          <div className="notice bad">{loadErr} <Link to="/signup">{tr('Start again')}</Link></div>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              {info ? tr('Signed in with Google as {email}.', { email: info.email }) : tr('Loading')} {tr('14-day trial. No card required.')}
            </p>
            <Field label={tr('Organization')} hint={tr('Your business name, as your team knows it.')}>
              <input type="text" required autoFocus value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} />
            </Field>
            <label className="check">
              <input type="checkbox" required checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
              <span>
                {tr('I have read and agree, on behalf of the organization, to the')}{' '}
                <a href="/terms" target="_blank" rel="noopener">{tr('Terms of Service and Privacy Policy')}</a>
              </span>
            </label>
            {s.err && <div className="notice bad" style={{ marginBottom: 12 }}>{s.err}</div>}
            <Button variant="primary" busy={s.busy} disabled={!info} type="submit" style={{ width: '100%' }}>{tr('Create account')}</Button>
          </>
        )}
      </form>
    </AuthLayout>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const [state, setState] = useState<'working' | 'ok' | 'bad'>('working');
  const [msg, setMsg] = useState('');
  const { refresh } = useSession();
  const once = useRef(false);
  useEffect(() => {
    if (once.current) return; // single-use token: never submit twice (StrictMode re-runs effects)
    once.current = true;
    post('/api/auth/verify-email', { token: sp.get('token') ?? '' })
      .then(async () => {
        setState('ok');
        await refresh();
      })
      .catch((e) => {
        setState('bad');
        setMsg((e as Error).message);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <AuthLayout>
      <div className="panel">
        <h2>{tr('Email verification')}</h2>
        {state === 'working' && <p>{tr('Verifying…')}</p>}
        {state === 'ok' && <div className="notice ok"><strong>{tr('Your email is verified.')}</strong><Link to="/">{tr('Continue to ZeroGap')}</Link></div>}
        {state === 'bad' && <div className="notice bad">{msg}</div>}
      </div>
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const s = useSubmit(async () => {
    await post('/api/auth/password/forgot', { email });
    setSent(true);
  });
  return (
    <AuthLayout>
      <form onSubmit={s.submit}>
        <h2>{tr('Reset your password')}</h2>
        {sent ? (
          <div className="notice ok">{tr('If an account exists for {email}, a reset link is on its way. It expires in one hour.', { email })}</div>
        ) : (
          <>
            <Field label={tr('Email')}><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
            {s.err && <div className="notice bad">{s.err}</div>}
            <Button variant="primary" busy={s.busy} type="submit" style={{ width: '100%' }}>{tr('Send reset link')}</Button>
          </>
        )}
        <p className="small" style={{ marginTop: 14 }}><Link to="/login">{tr('Back to sign in')}</Link></p>
      </form>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const s = useSubmit(async () => {
    await post('/api/auth/password/reset', { token: sp.get('token') ?? '', password });
    setDone(true);
  });
  return (
    <AuthLayout>
      <form onSubmit={s.submit}>
        <h2>{tr('Choose a new password')}</h2>
        {done ? (
          <div className="notice ok"><strong>{tr('Password updated.')}</strong>{tr('All other sessions were signed out.')} <Link to="/login">{tr('Sign in')}</Link></div>
        ) : (
          <>
            <Field label={tr('New password')} hint={tr('At least 12 characters. Two-factor authentication stays enabled.')}><input type="password" minLength={12} required autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus /></Field>
            {s.err && <div className="notice bad">{s.err}</div>}
            <Button variant="primary" busy={s.busy} type="submit" style={{ width: '100%' }}>{tr('Update password')}</Button>
          </>
        )}
      </form>
    </AuthLayout>
  );
}

export function AcceptInvite() {
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const { me, refresh } = useSession();
  const nav = useNavigate();
  const [info, setInfo] = useState<{ tenantName: string; email: string; role: string; accountExists: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState({ name: '', password: '' });
  useEffect(() => {
    get(`/api/invitations/${encodeURIComponent(token)}`).then(setInfo).catch((e) => setErr((e as Error).message));
  }, [token]);
  const qc = useQueryClient();
  const creating = !me && !!info && !info.accountExists;
  const wrongAccount = !!me && !!info && me.user.email.toLowerCase() !== info.email.toLowerCase();
  const s = useSubmit(async () => {
    const r = await post<{ tenantId: string }>('/api/invitations/accept', { token, ...(creating ? f : {}) });
    await refresh();
    nav(`/t/${r.tenantId}`);
  });
  const signOut = async () => {
    await post('/api/auth/logout');
    qc.clear();
    await refresh();
  };
  return (
    <AuthLayout>
      <form onSubmit={s.submit}>
        <h2>{tr('Join {org}', { org: info?.tenantName ?? tr('organization') })}</h2>
        {err && <div className="notice bad">{err}</div>}
        {info && (
          <>
            <p className="muted">{tr('Invitation for')} <strong className="mono">{info.email}</strong> {info.role === 'admin' ? tr('as an admin.') : tr('as a view-only member.')}</p>
            {!me && <GoogleButton next={`/invite?token=${encodeURIComponent(token)}`} invite={token} />}
            {info.accountExists && !me && <div className="notice">{tr('You already have an account.')} <Link to={`/login?next=${encodeURIComponent(`/invite?token=${token}`)}`}>{tr('Sign in to accept')}</Link></div>}
            {wrongAccount && (
              <div className="notice">
                {tr("You're signed in as {email}, but this invitation is for a different address.", { email: me!.user.email })}{' '}
                <div style={{ marginTop: 8 }}><Button type="button" size="sm" onClick={signOut}>{tr('Sign out to accept')}</Button></div>
              </div>
            )}
            {creating && (
              <>
                <Field label={tr('Your name')}><input type="text" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
                <Field label={tr('Password')} hint={tr('At least 12 characters.')}><input type="password" required minLength={12} autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
              </>
            )}
            {s.err && <div className="notice bad">{s.err}</div>}
            {((me && !wrongAccount) || creating) && <Button variant="primary" busy={s.busy} type="submit" style={{ width: '100%' }}>{tr('Accept invitation')}</Button>}
          </>
        )}
      </form>
    </AuthLayout>
  );
}
